import React from "react";
import Cookies from "js-cookie";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Data/UserContext";
import { AvatarWithXpCrown } from "../Xp/AvatarWithXpCrown";
import { resolveAvatarFrameSrc } from "../Profile/avatarFrame";
import {
  trackPurchaseFailed,
  trackShopView,
} from "../../utils/frontendTrackingEvents";
import FlyingCoins from "./FlyingCoins";
import FlyingRubies from "./FlyingRubies";
import HardOffers from "./HardOffers";
import SoftOffers from "./SoftOffers";
import FeatureUnlockPack from "./FeatureUnlockPack";
import AvatarFramesShop from "./AvatarFramesShop";
import ShopFooter from "./ShopFooter";
import { getAuthHeaders } from "../../utils/authHeaders";
import { VIRTUAL_CURRENCY_SHOP_NOTE, FTP_DISCLAIMER_SHORT } from "../../utils/virtualCurrencyDisclaimer";
import { respectDeviceSilentSwitch } from "../../utils/respectDeviceSilentSwitch";


/** Sounds */
const COINS_WON_SOUND_URL = "/assets/coins_won.mp3";
const RUBY_WON_SOUND_URL = "/assets/ruby_won.mp3";
const SHOP_PAGE_PATH = "/shop";

export default function Wallet({ rubiesOnly = false }: { rubiesOnly?: boolean } = {}) {
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const [isAnimatingCoins, setIsAnimatingCoins] = useState<boolean>(false);
  const [isAnimatingRubies, setIsAnimatingRubies] = useState<boolean>(false);
  const [isUpdatedCoins, setIsUpdatedCoins] = useState<boolean>(false);
  const [isUpdatedRubies, setIsUpdatedRubies] = useState<boolean>(false);
  const [rubyDecreaseFlash, setRubyDecreaseFlash] = useState<boolean>(false);

  const isShopEnabled = process.env.HIDE_SHOP ? process.env.HIDE_SHOP === 'false' : true

  const [coins, setCoins] = useState<number>(0);
  const [rubies, setRubies] = useState<number>(0);
  const [hasHardOffers, setHasHardOffers] = useState<boolean>(false);
  const coinAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rubyAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rubyDecreaseFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevCoinsRef = useRef<number>(0);
  const prevRubiesRef = useRef<number>(0);
  /** Only animate on load when we know the balance increased from a purchase or bonus, not on every Shop open */
  const shouldAnimateWalletRef = useRef<boolean>(false);
  const coinsWinSoundRef = useRef<HTMLAudioElement | null>(null);
  const rubyWonSoundRef = useRef<HTMLAudioElement | null>(null);

  const playCoinsWinSound = useCallback(() => {
    try {
      respectDeviceSilentSwitch();
      const audio = coinsWinSoundRef.current ?? new Audio(COINS_WON_SOUND_URL);
      coinsWinSoundRef.current = audio;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch (_) {}
  }, []);

  const playRubyWonSound = useCallback(() => {
    try {
      respectDeviceSilentSwitch();
      const audio = rubyWonSoundRef.current ?? new Audio(RUBY_WON_SOUND_URL);
      rubyWonSoundRef.current = audio;
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch (_) {}
  }, []);

  useEffect(() => {
    return () => {
      if (coinAnimationTimeoutRef.current != null) clearTimeout(coinAnimationTimeoutRef.current);
      if (rubyAnimationTimeoutRef.current != null) clearTimeout(rubyAnimationTimeoutRef.current);
      if (rubyDecreaseFlashTimeoutRef.current != null) clearTimeout(rubyDecreaseFlashTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    trackShopView({ user_id: user?.userId, pathname: SHOP_PAGE_PATH });
  }, [user?.userId]);

  function triggerCoinAnimation() {
    setIsAnimatingCoins(true);
    setIsUpdatedCoins(true);
    if (coinAnimationTimeoutRef.current != null) clearTimeout(coinAnimationTimeoutRef.current);
    coinAnimationTimeoutRef.current = setTimeout(() => {
      setIsAnimatingCoins(false);
      setIsUpdatedCoins(false);
      coinAnimationTimeoutRef.current = null;
    }, 3000);
  }

  function triggerRubyAnimation() {
    setIsAnimatingRubies(true);
    setIsUpdatedRubies(true);
    if (rubyAnimationTimeoutRef.current != null) clearTimeout(rubyAnimationTimeoutRef.current);
    rubyAnimationTimeoutRef.current = setTimeout(() => {
      setIsAnimatingRubies(false);
      setIsUpdatedRubies(false);
      rubyAnimationTimeoutRef.current = null;
    }, 3000);
  }

  // Play sound when animation state turns true (fallback for updateCoins/updateRubies; primary play is in click handlers via playCoinsWinSound/playRubyWonSound)
  useEffect(() => {
    if (isAnimatingCoins) playCoinsWinSound();
  }, [isAnimatingCoins, playCoinsWinSound]);

  useEffect(() => {
    if (isAnimatingRubies) playRubyWonSound();
  }, [isAnimatingRubies, playRubyWonSound]);

  // Show balance animation only when balance increased from a purchase or bonus (not on every Shop open)
  useEffect(() => {
    if (coins > prevCoinsRef.current && shouldAnimateWalletRef.current) {
      triggerCoinAnimation();
      setTimeout(() => { shouldAnimateWalletRef.current = false; }, 0);
    }
    prevCoinsRef.current = coins;
  }, [coins]);

  useEffect(() => {
    if (rubies > prevRubiesRef.current && shouldAnimateWalletRef.current) {
      triggerRubyAnimation();
      setTimeout(() => { shouldAnimateWalletRef.current = false; }, 0);
    }
    prevRubiesRef.current = rubies;
  }, [rubies]);

  /** @param forceRefresh - when true, always fetch from API (e.g. after payment return) so we don't overwrite with stale user.wallet */
  async function getWallet(forceRefresh = false) {
    const session_token = Cookies.get("c8_session_token");
    if (!session_token) return;

    if (!forceRefresh && user?.wallet != null) {
      setCoins(user.wallet?.coins ?? 0);
      setRubies(user.wallet?.rubies ?? 0);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.API_URL}/account/${session_token}`,
        { method: "GET", headers: getAuthHeaders() }
      );

      if (response.status === 200) {
        const data = await response.json();
        const coinsVal = data.data?.wallet?.coins ?? 0;
        const rubiesVal = data.data?.wallet?.rubies ?? 0;
        setCoins(coinsVal);
        setRubies(rubiesVal);
        setUser((prev) =>
          prev
            ? {
                ...prev,
                wallet: { ...(prev.wallet ?? {}), coins: coinsVal, rubies: rubiesVal },
              }
            : null
        );
      }
    } catch (error) {
      console.error("Error fetching wallet:", error);
    }
  }

  const ANIMATE_WALLET_ON_OPEN_KEY = "c8_animate_wallet_on_open";

  useEffect(() => {
    const paymentRubies = sessionStorage.getItem("c8_payment_success_rubies");
    if (paymentRubies != null) {
      const num = Number(paymentRubies);
      if (!Number.isNaN(num)) setRubies(num);
      sessionStorage.removeItem("c8_payment_success_rubies");
      shouldAnimateWalletRef.current = true;
      getWallet(true).catch(console.error);
    } else if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(ANIMATE_WALLET_ON_OPEN_KEY) === "1") {
      sessionStorage.removeItem(ANIMATE_WALLET_ON_OPEN_KEY);
      shouldAnimateWalletRef.current = true;
      getWallet().catch(console.error);
    } else {
      getWallet().catch(console.error);
    }
  }, [user?.wallet]);

  // Refetch wallet and show animation when wallet is updated elsewhere (e.g. email bonus)
  useEffect(() => {
    const onWalletUpdate = () => {
      shouldAnimateWalletRef.current = true;
      getWallet(true).catch(console.error);
    };
    window.addEventListener("walletUpdate", onWalletUpdate);
    return () => window.removeEventListener("walletUpdate", onWalletUpdate);
  }, []);

  async function UpdateUserAmount(
    offerId: string,
    offerType: string,
    meta?: { pay_amount: number; receive_amount: number; offer_name?: string }
  ) {
    const session_token = Cookies.get("c8_session_token");
    if (!session_token) return;

    try {
      const res = await fetch(`${process.env.API_URL}/buy`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          user_session: session_token,
          offer_id: offerId,
        }),
      });

      const response = await res.json();
      const { data } = response;

      if (offerType === "soft") {
        if (res.status === 201 && data?.coins !== undefined) {
          updateCoins(data.coins);
          setRubies(data.rubies);
          // purchase_success: server Measurement Protocol (/buy soft conversion)
        } else {
          trackPurchaseFailed({
            user_id: user?.userId,
            product_id: offerId,
            product_type: "coin_pack",
            amount: meta?.pay_amount,
            currency: "RUBIES",
            error_type: "validation_error",
            pathname: SHOP_PAGE_PATH,
          });
        }
      }

      if (data?.rubies !== undefined && offerType === "hard") {
        updateRubies(data.rubies);
      }
    } catch (error) {
      console.error("Error updating user amount:", error);
      if (offerType === "soft") {
        trackPurchaseFailed({
          user_id: user?.userId,
          product_id: offerId,
          product_type: "coin_pack",
          amount: meta?.pay_amount,
          currency: "RUBIES",
          error_type: "network_error",
          pathname: SHOP_PAGE_PATH,
        });
      }
    }
  }

  function updateCoins(newCoins: number) {
    setCoins(newCoins);
    setIsAnimatingCoins(true);
    setIsUpdatedCoins(true);
    if (coinAnimationTimeoutRef.current != null) {
      clearTimeout(coinAnimationTimeoutRef.current);
    }
    coinAnimationTimeoutRef.current = setTimeout(() => {
      setIsAnimatingCoins(false);
      setIsUpdatedCoins(false);
      coinAnimationTimeoutRef.current = null;
    }, 3000);
  }

  function updateRubies(newRubies: number) {
    const isDecrease = newRubies < rubies;
    setRubies(newRubies);
    setUser((prev) =>
      prev
        ? {
            ...prev,
            wallet: {
              ...(prev.wallet ?? { coins: 0, rubies: 0 }),
              rubies: newRubies,
              coins: prev.wallet?.coins ?? 0,
            },
          }
        : null
    );
    if (isDecrease) {
      if (rubyDecreaseFlashTimeoutRef.current != null) clearTimeout(rubyDecreaseFlashTimeoutRef.current);
      setRubyDecreaseFlash(true);
      rubyDecreaseFlashTimeoutRef.current = setTimeout(() => {
        setRubyDecreaseFlash(false);
        rubyDecreaseFlashTimeoutRef.current = null;
      }, 600);
    } else {
      setIsAnimatingRubies(true);
      setIsUpdatedRubies(true);
      if (rubyAnimationTimeoutRef.current != null) {
        clearTimeout(rubyAnimationTimeoutRef.current);
      }
      rubyAnimationTimeoutRef.current = setTimeout(() => {
        setIsAnimatingRubies(false);
        setIsUpdatedRubies(false);
        rubyAnimationTimeoutRef.current = null;
      }, 3000);
    }
  }

  const formatCoins = (amount: number) => {
    return new Intl.NumberFormat('en-IN').format(amount);
  };

  return (
    <div className='w-[100%]'>
        {/* User Info Panel */}
      <div 
        className='h-[56px] w-[100%] flex flex-row justify-between items-center bg-[#472176] border-y border-[#1B062F] border-solid'
      >
        <button
          type="button"
          onClick={() => {
            window.dispatchEvent(new CustomEvent("closeShopDrawer"));
            navigate("/account");
          }}
          className='flex flex-row items-center pl-[7px] cursor-pointer bg-transparent border-none text-left'
          aria-label="Open account"
        >
          <AvatarWithXpCrown
            crown={user?.xp_week_crown}
            frameSrc={resolveAvatarFrameSrc("md", user?.avatar_frame_url, user?.avatar_frame_small_url)}
            className="block h-[40px] w-[40px] shrink-0"
          >
          <img
            className="h-full w-full rounded-none object-cover"
            src={user?.avatar}
            alt=""
            loading="lazy"
            decoding="async"
          />
          </AvatarWithXpCrown>
          <p className='ml-[10px] text-white font-[400] text-[14px] font-anton leading-[22px] uppercase'>
            {user?.player_name}
          </p>
        </button>

        <div className='flex flex-row items-center gap-[8px] pr-[7px]'>
          {/* Rubies Display */}
          <div 
            className={`flex flex-row items-center gap-[6px] px-[8px] py-[10px] relative transition-colors duration-300 ${
              rubyDecreaseFlash ? 'text-[#f87171]' : 'text-white'
            }`}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.10)'
            }}
          >
            <div className='relative'>
              <FlyingRubies isAnimating={isAnimatingRubies} />
              <img
                alt={'how many rubies the user has'}
                className='w-[20px] h-[20px] relative z-10'
                src='/assets/currency-ruby.png'
                loading="lazy"
                decoding="async"
              />
            </div>
            <p
              className={`font-chakra text-[14px] font-[700] transition-colors duration-300 ${
                isUpdatedRubies ? 'amount-increse-animation' : ''
              } ${rubyDecreaseFlash ? 'text-[#f87171]' : 'text-white'}`}
            >
              {rubies}
            </p>
          </div>

          {/* Game coins Display — hidden in leagues rubies-only shop */}
          {rubiesOnly ? null : (
          <div 
            className='flex flex-row items-center gap-[6px] px-[8px] py-[10px] relative'
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.10)'
            }}
            title={FTP_DISCLAIMER_SHORT}
          >
            <div className='relative'>
              <FlyingCoins isAnimating={isAnimatingCoins} />
              <img
                alt="Gold balls"
                className='relative z-10 h-[18px] w-[18px]'
                src='/assets/currency-gold-ball.png'
              />
            </div>
            <div className="flex flex-col items-start leading-none">
              <span className="font-anton text-[8px] font-normal uppercase tracking-[0%] text-white/60">
                GOLD BALLS
              </span>
              <p
                className={`font-chakra text-white text-[14px] font-[700] ${
                  isUpdatedCoins ? 'amount-increse-animation' : ''
                }`}
              >
                {formatCoins(coins)}
              </p>
            </div>
          </div>
          )}
        </div>
      </div>

      {isShopEnabled && (
        <>
          {/* GET RUBIES Section */}
          {/* {hasHardOffers && ( */}
            <div className="flex w-[100%] flex-col items-center bg-[#401A6E] py-2">
              <div className='flex h-[30px] w-[100%] flex-row items-center justify-center gap-[8px] font-anton text-[14px] font-[400] leading-[15px] text-white'>
                <img
                  className='w-[25px] h-[25px]'
                  src='/assets/currency-ruby.png'
                  alt='ruby icon'
                />
                <span className="font-anton">GET RUBIES</span>
                <img
                  className='w-[25px] h-[25px]'
                  src='/assets/currency-ruby.png'
                  alt='ruby icon'
                />
              </div>
              <p className="px-3 pb-1 text-center font-chakra text-[11px] leading-snug text-white/55 normal-case">
                {VIRTUAL_CURRENCY_SHOP_NOTE}
              </p>
            </div>
          {/* )} */}
          <HardOffers 
            updateRubies={updateRubies} 
            setIsAnimatingRubies={setIsAnimatingRubies}
            onOffersLoaded={setHasHardOffers}
          />

          {/* GET GOLD BALLS — always shown, including when opened short on rubies */}
          <div className="flex w-[100%] flex-col items-center bg-[#401A6E] py-2">
            <div className='flex h-[30px] w-[100%] flex-row items-center justify-center gap-[8px] font-anton text-[14px] font-[400] leading-[15px] text-white'>
              <img
                className='h-[22px] w-[22px]'
                src='/assets/currency-gold-ball.png'
                alt='gold ball icon'
              />
              <span className="font-anton">GET GOLD BALLS</span>
              <img
                className='h-[22px] w-[22px]'
                src='/assets/currency-gold-ball.png'
                alt='gold ball icon'
              />
            </div>
            <p className="px-3 pb-1 text-center font-chakra text-[11px] leading-snug text-white/55 normal-case">
              {VIRTUAL_CURRENCY_SHOP_NOTE}
            </p>
          </div>
          <SoftOffers
            setIsAnimatingCoins={setIsAnimatingCoins}
            onPlayCoinsSound={playCoinsWinSound}
            totalRubies={rubies}
            updateUserAmount={UpdateUserAmount}
          />

          {/* GET PACKS Section */}
          <div className='w-[100%] h-[30px] font-[400] text-[14px] font-anton leading-[15px] bg-[#401A6E] text-white flex flex-row items-center justify-center gap-[8px] py-4'>
            <span className="font-anton">GET PACKS</span>
          </div>
          <div className="flex w-[100%] items-center justify-evenly gap-0.5 overflow-x-auto overflow-y-hidden bg-inherit px-[3px]">
            <FeatureUnlockPack
              rubies={rubies}
              updateRubies={updateRubies}
              setIsAnimatingRubies={setIsAnimatingRubies}
            />
          </div>

          {rubiesOnly ? null : <AvatarFramesShop rubies={rubies} coins={coins} />}
          
          {/* Footer */}
          <ShopFooter className="px-3 my-[18px]" />
        </>
      )}
    </div>
  );
}
